import React, { useState } from "react";
import usePortfolioStore from "../../../store/builders/usePortfolioStore";
import { UploadCloud, Trash2, Loader2 } from "lucide-react"; 
import { toast } from "react-hot-toast"; 

const PortfolioTile = () => {
  const { uploadPortfolioImage, isUploading: storeIsUploading, fetchPortfolio } = usePortfolioStore();
  const [images, setImages] = useState([]); // Stores { file, url, description, id (temp client id for key) }
  const [isProcessingAll, setIsProcessingAll] = useState(false); // Local loading for the "Upload All" button

  const maxFileSize = 5 * 1024 * 1024; // 5 MB
  const acceptedFormats = [
    "image/jpeg",
    "image/png",
    "image/webp",
    "image/jpg",
  ];
  const maxUploads = 25; // Max files to select at once (UI limit)

  const handleFiles = (selectedFiles) => {
    let newImageObjects = [];
    let errors = [];

    if (images.length + selectedFiles.length > maxUploads) {
      errors.push(
        `You can select up to ${maxUploads} images in total. Current: ${images.length}, Trying to add: ${selectedFiles.length}.`
      );
    }

    for (const file of selectedFiles) {
      if (images.length + newImageObjects.length >= maxUploads) {
        // This check ensures we don't exceed the maxUploads even if initial check passed (e.g., user selects 30 files)
        if (!errors.some((e) => e.includes("limit")))
          errors.push(`Image limit of ${maxUploads} reached.`);
        break;
      }
      if (!acceptedFormats.includes(file.type)) {
        errors.push(`File "${file.name}" has an unsupported format.`);
        continue;
      }
      if (file.size > maxFileSize) {
        errors.push(`File "${file.name}" exceeds the 5MB size limit.`);
        continue;
      }
      newImageObjects.push({
        id: Date.now() + Math.random(), // Temporary unique ID for React key
        file: file,
        url: URL.createObjectURL(file),
        description: "",
      });
    }

    if (errors.length > 0) {
      toast.error(
        <div>
          <p className="font-semibold">Could not add all files:</p>
          <ul className="list-disc list-inside text-sm">
            {errors.map((err, i) => (
              <li key={i}>{err}</li>
            ))}
          </ul>
        </div>,
        { duration: 5000 }
      );
    }

    setImages((prev) => [...prev, ...newImageObjects]);
  };

  const handleFileChange = (event) => {
    const files = Array.from(event.target.files);
    handleFiles(files);
    event.target.value = null; // Reset file input
  };

  const handleDrop = (event) => {
    event.preventDefault();
    event.stopPropagation();
    const files = Array.from(event.dataTransfer.files);
    handleFiles(files);
  };

  const handleRemoveImage = (imageId) => {
    const imageToRemove = images.find((img) => img.id === imageId);
    if (imageToRemove) {
      URL.revokeObjectURL(imageToRemove.url); // Clean up blob URL
    }
    setImages((prev) => prev.filter((img) => img.id !== imageId));
  };

  const handleDescriptionChange = (imageId, value) => {
    const uppercasedValue = value.toUpperCase().slice(0, 15);
    setImages((prev) =>
      prev.map((img) =>
        img.id === imageId ? { ...img, description: uppercasedValue } : img
      )
    );
  };

  const handleUploadAll = async () => {
    if (images.length === 0 || storeIsUploading || isProcessingAll) return;

    setIsProcessingAll(true);
    let successfulUploads = 0;

    for (const imageObj of images) {
      // The store's isUploading will be true during this individual call
      const result = await uploadPortfolioImage(
        imageObj.file,
        imageObj.description
      );
      if (result.success) {
        successfulUploads++;
      }
    }

    setIsProcessingAll(false);

    // Refresh gallery after all uploads
    await fetchPortfolio();

    if (successfulUploads === images.length && images.length > 0) {
      toast.success("All selected images uploaded successfully!");
      setImages([]); // Clear the list after all successful uploads
    } else if (successfulUploads > 0) {
      toast.info(
        `${successfulUploads} of ${images.length} images uploaded. Some may have failed.`
      );
      if (successfulUploads === 0 && images.length > 0) {
        toast.error("Upload failed. Please check errors and try again.");
      }
    } else if (images.length > 0 && successfulUploads === 0) {
      // Toasts for individual errors are already shown by the store.
      // No need for an additional "all failed" toast here unless desired.
    }
  };

  const isUploadDisabled =
    images.length === 0 || storeIsUploading || isProcessingAll;

  return (
    <div className="py-1 text-xs sm:text-sm">
      {/* Header */}
      <div className="flex items-center justify-between gap-4 mb-2 border-b border-gray-400 pb-2">
        <h2 className="text-sm font-semibold">Portfolio Upload</h2>
        <div className="flex items-center gap-2">
          {/* Compact Upload Button (Replacing Big Box) */}
          <button
            onClick={() => document.getElementById('portfolio-file-input').click()}
            disabled={storeIsUploading || isProcessingAll}
            className="flex items-center gap-1.5 px-3 py-1.5 bg-blue-600 text-white text-xs font-bold rounded-md hover:bg-blue-700 transition shadow-sm disabled:opacity-50"
          >
            <UploadCloud className="w-3.5 h-3.5" />
            + Upload
          </button>

          {images.length > 0 && (
            <button
              className={`px-3 py-1.5 bg-green-600 text-white rounded-md hover:bg-green-700 text-xs font-bold transition-colors flex items-center gap-1.5 ${isUploadDisabled ? "opacity-50 cursor-not-allowed" : ""}`}
              onClick={handleUploadAll}
              disabled={isUploadDisabled}
            >
              {storeIsUploading || isProcessingAll ? (
                <Loader2 className="w-3.5 h-3.5 animate-spin" />
              ) : (
                <UploadCloud className="w-3.5 h-3.5" />
              )}
              {storeIsUploading || isProcessingAll ? "Uploading..." : `Confirm (${images.length})`}
            </button>
          )}
        </div>
      </div>

      {/* Hidden File Input */}
      <input
        id="portfolio-file-input"
        type="file"
        accept={acceptedFormats.join(",")}
        multiple
        className="hidden"
        onChange={handleFileChange}
        disabled={storeIsUploading || isProcessingAll}
      />

      {/* Selected Images for Upload */}
      {images.length > 0 && (
        <div className="mt-6 space-y-4">
          <h4 className="text-xs font-bold text-gray-500 uppercase tracking-wider">
            Selected Images ({images.length})
          </h4>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {images.map((image) => (
              <div
                key={image.id}
                className="flex items-start gap-3 p-3 border border-gray-200 rounded-lg bg-white shadow-sm transition-all hover:border-blue-200"
              >
                <div className="relative w-16 h-16 flex-shrink-0">
                  <img
                    src={image.url}
                    alt="Preview"
                    className="w-full h-full object-cover rounded-md border border-gray-100 shadow-sm"
                  />
                </div>
                <div className="flex-grow min-w-0">
                  <textarea
                    value={image.description}
                    onChange={(e) =>
                      handleDescriptionChange(image.id, e.target.value)
                    }
                    placeholder="Add location/description..."
                    className="w-full p-2 border border-gray-200 rounded-md text-xs focus:outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-100 resize-none"
                    rows={2}
                    disabled={storeIsUploading || isProcessingAll}
                    maxLength={15}
                  />
                  <div className="flex justify-between items-center mt-1">
                    <span className="text-[10px] text-gray-400 truncate max-w-[120px]">
                      {image.file.name}
                    </span>
                    <span className="text-[10px] font-medium text-gray-500">
                      {image.description.length}/15
                    </span>
                  </div>
                </div>
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    handleRemoveImage(image.id);
                  }}
                  disabled={storeIsUploading || isProcessingAll}
                  className="p-1.5 text-red-500 hover:text-white hover:bg-red-500 rounded-md transition-colors disabled:opacity-50"
                  title="Remove image"
                >
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default PortfolioTile;
